import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSettings } from "../contexts/SettingsContext";

// Dave 2026-08-06 → 2026-02-15 · Configurable reveal delay for ALL chart
// hover tooltips. Cursor entering a chart data point starts a timer of
// `platform_settings.hover_reveal_seconds` (default 2 s); if the user
// moves off before the timer completes, the tooltip never renders. This
// keeps sweep-across mouse motion silent instead of firing a burst of
// tooltip flashes.
//
// Step 7.6 (2026-02-15) — The delay is now read from SettingsContext at
// render time so Platform Admins can retune it live from the Settings
// modal without a page reload. Explicit `delay` prop on
// <DelayedTooltipContent> still overrides the context value (for special-
// case tooltips that want a shorter or longer delay).
//
// IMPLEMENTATION NOTE — Recharts identifies its children (Tooltip, XAxis,
// Bar, Line, etc.) by comparing `child.type` against the actual imported
// component reference. Wrapping <Tooltip> in a custom component therefore
// breaks Recharts' child detection and stops bars/lines/areas from
// rendering. Instead, we keep the real <Tooltip> as a direct child of
// each chart and inject the delay via its `content` prop:
//
//   <Tooltip content={<DelayedTooltipContent
//                       inner={<CustomTooltipElem />}
//                       // OR default rendering:
//                       formatter={...} labelFormatter={...}
//                       contentStyle={...} labelStyle={...}
//                     />} />

const DEFAULT_DELAY_MS = 2000;

/**
 * useHoverDelay(active, delay, visibleMs, resetKey)
 * ----------------------------------------------------------------------
 * Returns a boolean `ready` that flips true only after `active` has been
 * true continuously for `delay` ms. Flips back to false immediately when
 * `active` goes false.
 *
 * Optional Item 3 (Dave 2026-02-15) — when `visibleMs > 0`, `ready` also
 * flips back to false `visibleMs` ms AFTER it first goes true (auto-
 * dismiss). Pass `resetKey` (typically the current data-point label /
 * year) so that moving to a new bar restarts BOTH the reveal delay and
 * the visibility window — each new data point gets its own fresh
 * `delay + visibleMs` cycle rather than inheriting the outgoing timer.
 */
export function useHoverDelay(active, delay = DEFAULT_DELAY_MS, visibleMs = 0, resetKey = null) {
    const [ready, setReady] = useState(false);
    const revealTimerRef = useRef(null);
    const hideTimerRef = useRef(null);

    useEffect(() => {
        // Clear any pending timers from a previous cycle first.
        if (revealTimerRef.current) {
            clearTimeout(revealTimerRef.current);
            revealTimerRef.current = null;
        }
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }

        if (!active) {
            setReady(false);
            return undefined;
        }

        // Start reveal delay
        revealTimerRef.current = setTimeout(() => {
            setReady(true);
            revealTimerRef.current = null;
            // Start auto-hide timer once revealed (only if configured).
            if (visibleMs > 0) {
                hideTimerRef.current = setTimeout(() => {
                    setReady(false);
                    hideTimerRef.current = null;
                }, visibleMs);
            }
        }, delay);

        return () => {
            if (revealTimerRef.current) {
                clearTimeout(revealTimerRef.current);
                revealTimerRef.current = null;
            }
            if (hideTimerRef.current) {
                clearTimeout(hideTimerRef.current);
                hideTimerRef.current = null;
            }
        };
    }, [active, delay, visibleMs, resetKey]);

    return ready;
}

// Hover-intent for click-to-open popovers: start(fn) runs fn after the
// platform hover delay; cancel() aborts (call on mouse leave and on click).
export function useHoverIntent() {
    const { settings } = useSettings();
    const ms = Math.max(0, Number(settings.hover_reveal_seconds ?? 2)) * 1000;
    const timer = useRef(null);
    const cancel = useCallback(() => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = null;
    }, []);
    const start = useCallback((fn) => {
        cancel();
        timer.current = setTimeout(() => { timer.current = null; fn(); }, ms);
    }, [cancel, ms]);
    useEffect(() => cancel, [cancel]);
    return { start, cancel };
}

/**
 * <DelayedTooltipContent … /> — pass this INSIDE Recharts <Tooltip content={…}>.
 *
 * Two modes:
 *   1. If `inner` is a React element, the delayed rendering clones that
 *      element with Recharts-injected props (active/payload/label…).
 *   2. Otherwise, renders a default styled tooltip using the same
 *      `formatter` / `labelFormatter` / `contentStyle` / `labelStyle`
 *      semantics as the built-in Recharts Tooltip.
 */
export function DelayedTooltipContent(props) {
    const {
        active,
        payload,
        label,
        coordinate,
        viewBox,
        inner,
        delay,
        formatter,
        labelFormatter,
        contentStyle,
        labelStyle,
        itemStyle,
        filter,
        forceLabel,
    } = props;

    // Read the configurable delay from SettingsContext. If the caller
    // passes an explicit `delay` prop it wins; otherwise use the platform
    // setting (in seconds) times 1000.
    const { settings } = useSettings();
    const resolvedDelay =
      typeof delay === "number"
        ? delay
        : Math.max(0, Number(settings.hover_reveal_seconds ?? 2)) * 1000;
    // Item 3 (Dave 2026-02-15) — after the tooltip first appears it
    // auto-dismisses `hover_visible_seconds` later (default 5 s, tunable
    // from Platform Settings modal → Chart Reveals). Moving to a new
    // data point restarts both timers (see `resetKey={label}` below).
    const resolvedVisibleMs =
      Math.max(0, Number(settings.hover_visible_seconds ?? 5)) * 1000;

    const show = useHoverDelay(active, resolvedDelay, resolvedVisibleMs, label);
    // A click (forceLabel === label) reveals immediately, even mid-delay.
    const forced = forceLabel !== undefined && forceLabel !== null && forceLabel === label;
    if (!active || !(show || forced) || !payload || payload.length === 0) return null;

    if (inner && React.isValidElement(inner)) {
        // 2026-02-15 (Dave) — forward `coordinate` and `viewBox` (Recharts-
        // injected) so the inner tooltip can self-flip when the cursor is
        // near the right edge of the chart, preventing the tooltip from
        // getting clamped to the far-left of the plot area.
        return React.cloneElement(inner, { active, payload, label, coordinate, viewBox });
    }

    const displayLabel = labelFormatter ? labelFormatter(label) : label;
    const baseWrap = {
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: 0,
        padding: "6px 10px",
        fontSize: 12,
        lineHeight: 1.4,
    };
    return (
        <div style={{ ...baseWrap, ...contentStyle }}>
            {displayLabel !== undefined && displayLabel !== null && displayLabel !== "" && (
                <div style={{ marginBottom: 4, ...labelStyle }}>{displayLabel}</div>
            )}
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                {payload.map((entry, i) => {
                    // 2026-02-28 (Dave) — Allow callers to exclude entire
                    // series from the tooltip payload via a `filter`
                    // callback.  Used on the Funding Level Analysis chart
                    // to hide the "Projected Funding %" row (already
                    // labelled directly on the chart) — but the same
                    // hook is useful anywhere a series is redundant.
                    // Callback contract: return TRUE to keep, FALSE to
                    // exclude.  Both the destructured `filter` and the
                    // props-bag are checked so accidental spread
                    // signatures still land on the callback.
                    const filterFn = typeof filter === "function"
                        ? filter
                        : (typeof props.filter === "function" ? props.filter : null);
                    if (filterFn && !filterFn(entry, i, payload)) {
                        return null;
                    }
                    let value = entry.value;
                    let name = entry.name;
                    if (formatter) {
                        const out = formatter(entry.value, entry.name, entry, i, payload);
                        if (Array.isArray(out)) {
                            [value, name] = out;
                        } else {
                            value = out;
                        }
                    }
                    return (
                        <li
                            key={i}
                            style={{
                                color: entry.color,
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 12,
                                ...itemStyle,
                            }}
                        >
                            <span>{name} :</span>
                            <span>{value}</span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default DelayedTooltipContent;
